/**
 * Credit cost constants and helper functions
 * These values match the backend credit_service.py
 */

export const CREDIT_COSTS = {
  /** Credits charged per lead generated */
  LEAD_GENERATION: 10,
  /** Credits charged per enrichment cell */
  ENRICHMENT: 5,
} as const;

/**
 * Calculate the credit cost for lead generation
 * @param count Number of leads to generate
 */
export const calculateLeadCost = (count: number): number =>
  count * CREDIT_COSTS.LEAD_GENERATION;

/**
 * Calculate the credit cost for enrichment
 * @param rowCount Number of rows to enrich
 */
export const calculateEnrichmentCost = (rowCount: number): number =>
  rowCount * CREDIT_COSTS.ENRICHMENT;

/** Most people a campaign may ask for; the backend refuses more. */
export const PEOPLE_WANTED_MAX = 10_000;

const withoutFloatNoise = (credits: number): number => Math.round(credits * 1e6) / 1e6;

/** Whole credits with locale separators; a non-zero amount under one credit reads "<1". */
export const formatCredits = (credits: number): string => {
  const exact = withoutFloatNoise(credits);
  if (exact !== 0 && Math.abs(exact) < 1) return '<1';

  return (Math.trunc(exact) || 0).toLocaleString();
};

/** A cost in whole credits, rounded up so it is never shown short. */
export const formatCreditsNeeded = (credits: number): string =>
  Math.ceil(withoutFloatNoise(credits)).toLocaleString();

/** A balance in whole credits, rounded down so it is never shown long. */
export const formatCreditsHeld = (credits: number): string =>
  Math.floor(withoutFloatNoise(credits)).toLocaleString();

/** What each credit ledger action reads as on the usage list. */
export const CREDIT_ACTION_LABELS: Record<string, string> = {
  lead_generation: 'Lead generation',
  enrichment: 'Enrichment',
  chat: 'Chat',
  conference_setup: 'Conference research',
  attendee_processed: 'Attendees checked',
  email_found: 'Emails found',
  person_sequence: 'People sequenced',
  credits_moved_in: 'Credits brought by a new member',
  credits_moved_out: 'Credits moved to a team workspace',
  hold: 'Held for research',
};
